import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type {
  AutoPriorityMode,
  BatchHatchResponse,
  Egg,
  HatchResult,
  InventoryEntry,
  PriorityEggState,
} from '../lib/types';
import { Button, EmptyState, ErrorState, Modal, Panel } from '../components/ui';
import { EggSlot, EmptyEggSlot } from '../components/game';
import { EggArt } from '../components/EggArt';
import { HatchModal } from '../components/HatchModal';
import { HatchReview } from '../components/breeding/HatchReview';
import { formatDuration } from '../lib/format';

interface EggsResponse {
  eggs: Egg[];
  hatcherySlots: number;
  /** Which egg receives warmth when this trainer warms somebody else's. */
  priority: PriorityEggState;
}

export function HatcheryPage() {
  const { refresh, celebrate, celebrateBadges } = useSession();
  const { push } = useToast();
  const state = useAsync<EggsResponse>(() => api.get<EggsResponse>('/eggs'));
  const inventory = useAsync<{ items: InventoryEntry[] }>(() => api.get('/me/inventory'));

  const [hatching, setHatching] = useState<{ egg: Egg; result: HatchResult } | null>(null);
  const [batch, setBatch] = useState<BatchHatchResponse | null>(null);
  const [picking, setPicking] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pinning, setPinning] = useState(false);

  const eggs = state.data?.eggs ?? [];
  const slots = state.data?.hatcherySlots ?? 4;
  const incubating = eggs.filter((egg) => egg.status === 'INCUBATING');
  const stored = eggs.filter((egg) => egg.status === 'STORED');
  const boosts = inventory.data?.items.find((item) => item.key === 'incubator-boost')?.quantity ?? 0;
  const priority = state.data?.priority ?? null;
  const priorityEgg = priority?.eggId ? incubating.find((egg) => egg.id === priority.eggId) : undefined;

  /**
   * Pinning an egg, or changing what happens when the pinned one hatches.
   *
   * One endpoint takes both because they are one control on screen. The server
   * answers with the *resolved* pin rather than what was asked for, so an egg
   * that hatched in another tab shows the fallback immediately instead of a
   * pin that silently does nothing.
   */
  async function setPriority(input: { eggId?: string | null; mode?: AutoPriorityMode }) {
    setPinning(true);
    try {
      const result = await api.patch<PriorityEggState>('/eggs/priority', input);
      state.setData((current) => (current ? { ...current, priority: { ...current.priority, ...result } } : current));
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not set your Priority Egg.');
    } finally {
      setPinning(false);
    }
  }

  async function hatch(egg: Egg) {
    setBusy(egg.id);
    try {
      const result = await api.post<HatchResult>('/eggs/hatch', { eggId: egg.id });
      // The achievement celebration is queued until the hatch reveal is
      // dismissed — two stacked modals is worse than either one alone.
      setHatching({ egg, result });
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not hatch that egg.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Cracks everything that is ready at once and opens the review.
   *
   * Separate from `hatch` rather than a loop over it: one egg deserves the
   * reveal animation, and twenty deserve a summary. Running the single-egg
   * modal twenty times would be twenty dismissals.
   */
  async function hatchAll() {
    setBusy('all');
    try {
      const result = await api.post<BatchHatchResponse>('/eggs/hatch-batch', {});
      setBatch(result);
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not hatch those.');
    } finally {
      setBusy(null);
    }
  }

  async function incubate(eggId: string, slotIndex: number) {
    setBusy(eggId);
    try {
      await api.post('/eggs/incubate', { eggId, slotIndex });
      push('success', 'Egg placed in the incubator.', '◓');
      setPicking(null);
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not place that egg.');
    } finally {
      setBusy(null);
    }
  }

  async function store(eggId: string) {
    setBusy(eggId);
    try {
      await api.post('/eggs/store', { eggId });
      push('info', 'Egg returned to storage. Its progress is saved.');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not move that egg.');
    } finally {
      setBusy(null);
    }
  }

  async function boost(eggId: string) {
    setBusy(eggId);
    try {
      const result = await api.post<{ message: string; isReady: boolean }>('/shop/use', {
        itemKey: 'incubator-boost',
        eggId,
      });
      push('reward', `Warm Blanket used — ${result.message}`, '◆');
      state.reload();
      inventory.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not use that item.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const occupiedSlots = new Set(incubating.map((egg) => egg.slotIndex));
  const freeSlots = Array.from({ length: slots }, (_, index) => index).filter(
    (index) => !occupiedSlots.has(index),
  );

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Hatchery</h1>
          <div className="page-head__sub">
            Eggs warm on their own, but every trainer who visits your profile speeds them up.
          </div>
        </div>
        <Link className="btn btn--primary" to="/eggs">
          Get more eggs
        </Link>
      </div>

      {/* --- The Priority Egg ------------------------------------------------
          Above the incubators, because it changes what every one of them means:
          this is the egg that moves when the player goes out and warms somebody
          else's. The rule is stated in words rather than left to be inferred —
          "your progress went somewhere predictable" is the whole point, and an
          unexplained rule reads as randomness. */}
      {incubating.length > 0 && priority && (
        <Panel
          title="🔥 Priority Egg"
          hint="Warm another trainer's egg and this one warms too"
        >
          <div className="priority-egg">
            {priorityEgg ? (
              <div className="priority-egg__current pxwell">
                <EggArt appearance={priorityEgg.appearance} size="sm" state={priorityEgg.isReady ? 'ready' : 'idle'} />
                <div className="stack-sm" style={{ flex: 1, minWidth: 0 }}>
                  <strong>{priorityEgg.familyName}</strong>
                  <span className="tiny mono muted">
                    {priorityEgg.progress} / {priorityEgg.requiredProgress} hatch points
                  </span>
                  {priorityEgg.isReady && (
                    <span className="chip chip--sm chip--on">🥚 Ready to hatch!</span>
                  )}
                </div>
                {!priority.pinned && <span className="chip chip--sm">auto</span>}
              </div>
            ) : (
              <p className="tiny muted" style={{ margin: 0 }}>
                Nothing incubating yet — put an egg in a slot and it will warm along with the ones
                you help.
              </p>
            )}

            {/* Never auto-hatched. An egg becoming ready is an invitation, and
                opening it is the moment the whole loop is for. */}
            <p className="tiny muted" style={{ margin: 0 }}>{priority.explain}</p>

            <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
              {(priority.modes ?? []).map((mode) => (
                <button
                  key={mode.key}
                  type="button"
                  className={`chip ${priority.mode === mode.key ? 'chip--on' : ''}`}
                  title={mode.help}
                  disabled={pinning}
                  onClick={() => void setPriority({ mode: mode.key })}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            {incubating.length > 1 && (
              <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
                {incubating.map((egg) => (
                  <button
                    key={egg.id}
                    type="button"
                    className={`chip ${priority.eggId === egg.id ? 'chip--on' : ''}`}
                    disabled={pinning || egg.isReady}
                    title={egg.isReady ? 'Ready to hatch — open it first' : `Pin ${egg.familyName}`}
                    onClick={() => void setPriority({ eggId: egg.id })}
                  >
                    🔥 {egg.familyName}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Panel>
      )}

      <Panel title="◓ Incubators" hint={`${incubating.length} / ${slots} slots in use`}>
        {/* Batch hatching — design PART 26. Only offered when it saves clicks. */}
        {incubating.filter((egg) => egg.isReady).length > 1 && (
          <Button
            variant="gold"
            block
            loading={busy === 'all'}
            onClick={() => void hatchAll()}
            style={{ marginBottom: 'var(--gap-sm)' }}
          >
            Hatch all {incubating.filter((egg) => egg.isReady).length} ready eggs
          </Button>
        )}
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))' }}>
          {Array.from({ length: slots }, (_, index) => {
            const egg = incubating.find((candidate) => candidate.slotIndex === index);
            if (!egg) {
              return (
                <EmptyEggSlot
                  key={index}
                  onClick={() => (stored.length > 0 ? setPicking(index) : undefined)}
                />
              );
            }
            return (
              <div key={egg.id} className="stack-sm">
                <EggSlot egg={egg} onHatch={(target) => void hatch(target)} busy={busy === egg.id} />
                <div className="row" style={{ gap: 4 }}>
                  {!egg.isReady && boosts > 0 && (
                    <Button size="sm" variant="gold" onClick={() => void boost(egg.id)} disabled={busy === egg.id}>
                      Blanket ({boosts})
                    </Button>
                  )}
                  {!egg.isReady && (
                    <Button size="sm" onClick={() => void store(egg.id)} disabled={busy === egg.id}>
                      Store
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {slots < 8 && (
          <p className="tiny muted center" style={{ marginTop: 'var(--gap)' }}>
            More incubator slots are available in the <Link to="/shop">Shop</Link>.
          </p>
        )}
      </Panel>

      <Panel title="◔ Egg storage" hint={`${stored.length} egg${stored.length === 1 ? '' : 's'}`}>
        {stored.length === 0 ? (
          <EmptyState
            art="◔"
            title="No eggs in storage"
            action={
              <Link className="btn btn--primary" to="/eggs">
                Visit the Egg Market
              </Link>
            }
          >
            Storage holds eggs you aren't incubating yet. Their progress is kept while they wait.
          </EmptyState>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
            {stored.map((egg) => (
              <div key={egg.id} className="pxframe egg-slot">
                <EggArt appearance={egg.appearance} size="sm" state="still" label={egg.familyName} />
                <div className="mono tiny center">{egg.familyName}</div>
                {egg.progress > 0 && (
                  <div className="tiny muted">
                    {egg.progress}/{egg.requiredProgress} steps
                  </div>
                )}
                <Button
                  size="sm"
                  variant="go"
                  disabled={freeSlots.length === 0 || busy === egg.id}
                  onClick={() => void incubate(egg.id, freeSlots[0]!)}
                >
                  {freeSlots.length === 0 ? 'Slots full' : 'Incubate'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {picking !== null && (
        <Modal onClose={() => setPicking(null)} labelledBy="pick-title">
          <h2 id="pick-title" style={{ marginBottom: 'var(--gap)' }}>
            Choose an egg for slot {picking + 1}
          </h2>
          {stored.length === 0 ? (
            <EmptyState art="◔" title="No eggs in storage" />
          ) : (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
              {stored.map((egg) => (
                <button
                  key={egg.id}
                  type="button"
                  className="pxframe egg-slot"
                  style={{ border: 0, cursor: 'pointer' }}
                  onClick={() => void incubate(egg.id, picking)}
                  disabled={busy === egg.id}
                >
                  <EggArt appearance={egg.appearance} size="sm" state="still" label={egg.familyName} />
                  <span className="mono tiny">{egg.familyName}</span>
                  <span className="tiny muted">
                    {formatDuration((egg.requiredProgress - egg.progress) * egg.stepSeconds)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}

      {batch && (
        <HatchReview
          batch={batch}
          onClose={() => setBatch(null)}
          onChanged={() => {
            state.reload();
            void refresh();
          }}
        />
      )}

      {hatching && (
        <HatchModal
          egg={hatching.egg}
          result={hatching.result}
          onClose={() => {
            const unlocked = hatching.result.unlocked;
            const badges = hatching.result.badges ?? [];
            setHatching(null);
            celebrate(unlocked);
            celebrateBadges(badges);
          }}
        />
      )}
    </div>
  );
}
